import { Card, ErrorState, LoadingState, Stack, useApi, useI18n, type TenantModuleResponse } from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * A tenant's modules (NR-11, decision 2026-11-01 #3), behind `platform.modules.manage`. Each module has a switch;
 * one not built in this release is shown switched off and disabled, with a note saying so. Switching one off keeps
 * its data, read only, which the hint says.
 */
export function ModulesCard({ tenantId }: { tenantId: number }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const key = ['platform', 'tenant', tenantId, 'modules'];

  const modules = useQuery({ queryKey: key, queryFn: () => api.platform.tenantModules(tenantId) });

  const change = useMutation({
    mutationFn: ({ kind, enabled }: { kind: TenantModuleResponse['kind']; enabled: boolean }) =>
      api.platform.switchTenantModule(tenantId, kind, enabled),
    onSuccess: (row) => queryClient.setQueryData<TenantModuleResponse[]>(key, (rows) =>
      rows?.map((current) => (current.kind === row.kind ? row : current))),
  });

  return (
    <Card title={t('modules.title')} hint={t('modules.hint')}>
      {modules.isPending ? <LoadingState /> : modules.isError ? (
        <ErrorState error={modules.error} onRetry={() => void modules.refetch()} />
      ) : (
        <Stack>
          {change.isError && <ErrorState error={change.error} />}
          {modules.data.map((module) => (
            <label key={module.kind} className="dg-checkbox">
              <input
                type="checkbox"
                checked={module.state === 'On'}
                disabled={!module.built || change.isPending}
                onChange={(event) => change.mutate({ kind: module.kind, enabled: event.target.checked })}
              />
              <span>{t(`modules.kind.${module.kind}`)}</span>
              {!module.built && <span className="dg-card__hint">{t('modules.notBuilt')}</span>}
              {module.built && module.state === 'Off' && <span className="dg-card__hint">{t('modules.off')}</span>}
            </label>
          ))}
        </Stack>
      )}
    </Card>
  );
}
